import { memo, useMemo } from 'react';
import { useTranslation } from '@aimsos/i18n';
import { DrawerKeyValueList, RichTextEditor } from '@aimsos/ui';

import { formatActivityTimestamp } from '../../utils/groupActivityByDate.js';
import NoteLinks from './NoteLinks.jsx';

import { Calendar, Pencil, ShieldCheck, User, UserCog } from 'lucide-react';

/**
 * A note's body on the left, its metadata on the right (CT-5528): author, role,
 * created, edited and scope, then the attached links.
 *
 * The body is the editor in `readOnly`, so a note renders exactly as it was
 * written (lists, bold) without this file parsing HTML itself. The scope word is
 * the backend's own value translated for display — never the other way round.
 */
const NoteDetails = memo(function NoteDetails({ note }) {
  const { t, i18n } = useTranslation('unified-profiles');
  const language = i18n?.language;

  const items = useMemo(() => {
    const stamp = (value) => (value ? formatActivityTimestamp(value, language) : '—');
    return [
      { key: 'author', icon: User, label: t('notes.meta.author'), value: note.author?.name ?? '—' },
      { key: 'role', icon: UserCog, label: t('notes.meta.role'), value: note.author?.role ?? '—' },
      { key: 'created', icon: Calendar, label: t('notes.meta.created'), value: stamp(note.createdAt) },
      { key: 'edited', icon: Pencil, label: t('notes.meta.edited'), value: stamp(note.editedAt) },
      { key: 'scope', icon: ShieldCheck, label: t('notes.meta.scope'), value: t(`notes.scope.${note.scope}`) },
    ];
  }, [note, language, t]);

  return (
    <div className="grid gap-6 sm:grid-cols-[minmax(0,1fr)_220px]" data-testid="note-details">
      <div className="min-w-0" data-testid="note-body">
        <RichTextEditor value={note.body} inputFormat="html" readOnly toolbarPreset="custom" />
      </div>
      <aside aria-label={t('notes.meta.title')} className="flex min-w-0 flex-col gap-4">
        <DrawerKeyValueList items={items} />
        <NoteLinks links={note.links} />
      </aside>
    </div>
  );
});

export default NoteDetails;
